import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import Logo from "@/components/Logo";
import { fmtDate } from "@/components/PostCard";

const BLANK = () => ({
  title: "",
  type: "article",
  category: "",
  tags: "",
  date: new Date().toISOString().slice(0, 10),
  cover_url: "",
  excerpt: "",
  content: "",
  published: true,
  featured: false,
});

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data.user);
      toast.success("Welcome back to the desk.");
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-band px-6">
      <form onSubmit={submit} className="w-full max-w-md border border-line bg-white p-8 sm:p-10">
        <Logo className="mb-6" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">Editorial Desk — Private</p>
        <h1 className="mb-8 mt-3 font-serif text-3xl text-navy">Sign in to manage writings.</h1>
        {error && (
          <p data-testid="admin-login-error" className="mb-6 border-l-2 border-destructive pl-4 text-sm text-destructive">
            {error}
          </p>
        )}
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Email</span>
          <input
            data-testid="admin-login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="admin-input"
          />
        </label>
        <label className="mt-5 block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Password</span>
          <input
            data-testid="admin-login-passkey-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="admin-input"
          />
        </label>
        <button data-testid="admin-login-submit-button" disabled={busy} className="btn-primary mt-8 w-full justify-center disabled:opacity-50">
          {busy ? "Signing in…" : "Enter the desk"}
        </button>
        <Link to="/" className="mt-6 inline-block text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-mute transition-colors hover:text-blue">
          ← Back to the portfolio
        </Link>
      </form>
    </div>
  );
}

function PostEditor({ initial, onDone, onCancel }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (f.id) {
        await api.put(`/posts/${f.id}`, payload);
        toast.success("Writing updated.");
      } else {
        await api.post("/posts", payload);
        toast.success("Writing saved to the desk.");
      }
      onDone();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-line bg-white p-8 sm:p-10">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">{f.id ? "Edit writing" : "New writing"}</p>
        <button type="button" onClick={onCancel} className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-mute transition-colors hover:text-navy">
          Cancel
        </button>
      </div>

      <label className="mt-6 block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Title</span>
        <input data-testid="admin-paper-title-input" required value={f.title} onChange={set("title")} className="admin-input" />
      </label>

      <div className="mt-6 grid gap-6 sm:grid-cols-3">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Type</span>
          <select value={f.type} onChange={set("type")} className="admin-input">
            <option value="paper">Research Paper</option>
            <option value="article">Essay</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Category</span>
          <input value={f.category} onChange={set("category")} className="admin-input" placeholder="Economic Analysis" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Date</span>
          <input type="date" value={f.date} onChange={set("date")} className="admin-input" />
        </label>
      </div>

      <label className="mt-6 block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Tags — comma separated</span>
        <input value={f.tags} onChange={set("tags")} className="admin-input" placeholder="Market Sizing, TDT" />
      </label>

      <label className="mt-6 block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Cover image URL — optional</span>
        <input value={f.cover_url} onChange={set("cover_url")} className="admin-input" placeholder="https://…" />
      </label>

      <label className="mt-6 block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Excerpt / abstract</span>
        <textarea rows={3} value={f.excerpt} onChange={set("excerpt")} className="admin-input resize-y" />
      </label>

      <label className="mt-6 block">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">
          Content — blank line between paragraphs · "## " for headings · "&gt; " for pull quotes
        </span>
        <textarea rows={14} value={f.content} onChange={set("content")} className="admin-input resize-y font-mono text-xs" />
      </label>

      <div className="mt-6 flex flex-wrap gap-8">
        <label className="flex items-center gap-3 text-sm text-navy-soft">
          <input type="checkbox" checked={f.published} onChange={set("published")} className="h-4 w-4 accent-[#2251FF]" />
          Published
        </label>
        <label className="flex items-center gap-3 text-sm text-navy-soft">
          <input type="checkbox" checked={f.featured} onChange={set("featured")} className="h-4 w-4 accent-[#2251FF]" />
          Featured
        </label>
      </div>

      <button data-testid="admin-paper-submit-button" disabled={busy} className="btn-primary mt-8 w-full justify-center disabled:opacity-50">
        {busy ? "Saving…" : f.id ? "Save changes" : "Save writing"}
      </button>
    </form>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("writings");
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState("");

  const loadPosts = useCallback(() => {
    api.get("/posts", { params: { status: "all" } }).then((r) => setPosts(r.data)).catch(() => {});
  }, []);
  const loadMessages = useCallback(() => {
    api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) {
      loadPosts();
      loadMessages();
    }
  }, [user, loadPosts, loadMessages]);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-navy-mute">Checking session…</p>
      </div>
    );
  }
  if (!user) return <LoginCard onLogin={setUser} />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this writing permanently?")) return;
    setBusyId(id);
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Deleted.");
      loadPosts();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusyId("");
    }
  };

  const delMsg = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/messages/${id}`);
      toast.success("Message removed.");
      loadMessages();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="min-h-screen bg-paper-band pb-24">
      <div className="wrap pt-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line pb-6">
          <div className="flex items-center gap-5">
            <Logo />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">Editorial Desk</p>
              <p className="font-mono text-[10px] text-navy-mute">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-mute transition-colors hover:text-blue">
              View site
            </Link>
            <button onClick={logout} className="btn-ghost !px-5 !py-2.5" data-testid="admin-logout-button">
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            {[
              { id: "writings", label: `Writings (${posts.length})` },
              { id: "messages", label: `Messages (${messages.length})` },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => { setTab(t.id); setEditing(null); }}
                className={`border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                  tab === t.id ? "border-blue bg-blue text-white" : "border-line bg-white text-navy-soft hover:border-blue hover:text-blue"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === "writings" && !editing && (
            <button
              data-testid="admin-add-paper-button"
              onClick={() => setEditing(BLANK())}
              className="btn-primary !px-5 !py-2.5"
            >
              <Plus size={14} /> New writing
            </button>
          )}
        </div>

        {tab === "writings" && (
          <div className="mt-8">
            {editing ? (
              <PostEditor
                initial={editing}
                onDone={() => { setEditing(null); loadPosts(); }}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <div className="border border-line bg-white">
                {posts.length === 0 && (
                  <p className="p-10 text-center font-serif italic text-navy-mute">No writings yet — start with the button above.</p>
                )}
                {posts.map((p) => (
                  <div key={p.id} data-testid="admin-post-row" className="flex flex-wrap items-center justify-between gap-4 border-b border-line p-5 last:border-b-0 hover:bg-paper-band/60">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-serif text-lg text-navy">{p.title}</p>
                      <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-mute">
                        {p.type === "paper" ? "Paper" : "Essay"} · {p.category} · {fmtDate(p.date)} ·{" "}
                        <span className={p.published ? "text-blue" : "text-navy-mute"}>{p.published ? "Published" : "Draft"}</span>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid={`admin-post-edit-button-${p.id}`}
                        onClick={() => setEditing({ ...p, tags: (p.tags || []).join(", ")} )}
                        className="border border-line bg-white px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-soft transition-colors hover:border-blue hover:text-blue"
                      >
                        <Pencil size={12} className="mr-2 inline" /> Edit
                      </button>
                      <button
                        data-testid={`admin-post-delete-button-${p.id}`}
                        disabled={busyId === p.id}
                        onClick={() => del(p.id)}
                        className="border border-line bg-white px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-soft transition-colors hover:border-destructive hover:text-destructive disabled:opacity-40"
                      >
                        <Trash2 size={12} className="mr-2 inline" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-8 space-y-4">
            {messages.length === 0 && (
              <p className="border border-line bg-white p-10 text-center font-serif italic text-navy-mute">No messages yet.</p>
            )}
            {messages.map((m) => (
              <div key={m.id} data-testid="admin-message-row" className="border border-line bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-serif text-lg text-navy">{m.name}</p>
                  <button
                    data-testid={`admin-message-delete-button-${m.id}`}
                    disabled={busyId === m.id}
                    onClick={() => delMsg(m.id)}
                    className="text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-mute transition-colors hover:text-destructive disabled:opacity-40"
                  >
                    <Trash2 size={12} className="mr-2 inline" /> Remove
                  </button>
                </div>
                <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-blue">{m.email}</p>
                {m.subject && <p className="mt-2 text-sm text-navy-soft">Re: {m.subject}</p>}
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-navy-soft">{m.message}</p>
                <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-mute">{fmtDate(m.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}